<script setup lang="ts">
  import { computed } from 'vue';
  import MpfDetails from '../MpfDetails/MpfDetails.vue';
  import type { MpfSlotPickerEmits, MpfSlotPickerProps, MpfSlotWeek } from './MpfSlotPicker.props';
  import { mpfSlotPickerDefaults } from './MpfSlotPicker.props';
  import {
    formatDayHeading,
    formatSlotAccessibleName,
    formatSlotTime,
    formatLongDate,
  } from '../../helpers/dates';

  const props = withDefaults(defineProps<MpfSlotPickerProps>(), mpfSlotPickerDefaults);
  const emit = defineEmits<MpfSlotPickerEmits>();

  defineOptions({
    name: 'MpfSlotPicker',
  });

  const weekHasSlots = (week: MpfSlotWeek) => week.days.some((day) => day.slots.length > 0);

  // A day with no slots is dropped rather than rendered empty — an expander that
  // opens onto nothing is worse than not offering it.
  const daysWithSlots = (week: MpfSlotWeek) => week.days.filter((day) => day.slots.length > 0);

  const hasAnySlots = computed(() => props.weeks.some(weekHasSlots));

  const slotCount = (week: MpfSlotWeek) =>
    week.days.reduce((total, day) => total + day.slots.length, 0);

  const weekTitle = (week: MpfSlotWeek) => `Week beginning ${formatLongDate(week.weekStartDate)}`;

  // Counts sit on the headings so the patient can see where the availability is
  // without opening every day group.
  const weekCountLabel = (week: MpfSlotWeek) => {
    const count = slotCount(week);
    return `${count} ${count === 1 ? 'appointment' : 'appointments'} available`;
  };

  const dayCountLabel = (count: number) =>
    `${count} ${count === 1 ? 'appointment' : 'appointments'}`;

  const onSelect = (slotId: string) => {
    if (props.loading) return;
    emit('select', slotId);
  };
</script>

<template>
  <div class="mpf-slot-picker" :class="{ 'mpf-slot-picker--loading': loading }">
    <p v-if="loading" class="mpf-slot-picker__status" role="status">Loading appointments…</p>

    <p v-else-if="!hasAnySlots" class="mpf-slot-picker__status" role="status">
      {{ emptyWeekMessage }}
    </p>

    <template v-else>
      <section
        v-for="week in weeks"
        :key="week.weekStartDate"
        class="mpf-slot-picker__week"
        :aria-label="weekTitle(week)"
      >
        <h2 class="mpf-slot-picker__week-title">{{ weekTitle(week) }}</h2>

        <p v-if="!weekHasSlots(week)" class="mpf-slot-picker__status">
          {{ emptyWeekMessage }}
        </p>
        <p v-else class="mpf-slot-picker__week-count">{{ weekCountLabel(week) }}</p>

        <MpfDetails
          v-for="day in daysWithSlots(week)"
          :key="day.date"
          :id="`slot-day-${day.date}`"
          :summary="`${formatDayHeading(day.date)} — ${dayCountLabel(day.slots.length)}`"
          :open="true"
        >
          <ul class="mpf-slot-picker__slots">
            <li v-for="slot in day.slots" :key="slot.slotId" class="mpf-slot-picker__slot">
              <button
                type="button"
                class="mpf-slot-picker__button"
                :class="{
                  'mpf-slot-picker__button--selected': slot.slotId === selectedSlotId,
                }"
                :aria-pressed="slot.slotId === selectedSlotId"
                :aria-label="formatSlotAccessibleName(day.date, slot.startsAt)"
                :disabled="loading"
                @click="onSelect(slot.slotId)"
              >
                {{ formatSlotTime(slot.startsAt) }}
              </button>
            </li>
          </ul>
        </MpfDetails>
      </section>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  @use './MpfSlotPicker.scss';
</style>
